/**
 * Mini-Basket Drawer
 * Feature: 016-mini-basket-drawer
 *
 * Provides read-only cart preview in a slide-out drawer
 * - BasketDrawer: Main drawer logic (fetch, render, focus trap, keyboard nav)
 * - BasketIcon: Badge count updates via cart:updated events
 */

class BasketDrawer {
  constructor(drawerEl) {
    this.drawerEl = drawerEl;
    this.overlayEl = drawerEl.querySelector('[data-basket-overlay]');
    this.contentEl = drawerEl.querySelector('[data-basket-content]');
    this.closeBtn = drawerEl.querySelector('[data-basket-close]');
    this.emptyCta = drawerEl.dataset.emptyCta || '/collections/all';
    this.cartUrl = drawerEl.dataset.cartUrl || '/cart';

    // State
    this.isOpen = false;
    this.isLoading = false;
    this.hasError = false;
    this.cartData = null;
    this.previouslyFocusedElement = null;

    // Translations
    this.translations = {
      emptyMessage: this.getTranslation('basket_drawer.empty_message', 'Your basket is empty'),
      continueShopping: this.getTranslation('basket_drawer.continue_shopping', 'Continue Shopping'),
      errorMessage: this.getTranslation('basket_drawer.error_message', 'Unable to load basket. Please try again.'),
      retryButton: this.getTranslation('basket_drawer.retry_button', 'Try Again'),
      viewBasket: this.getTranslation('basket_drawer.view_basket', 'View Basket')
    };

    // Touch gesture support for mobile
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.attachEventListeners();
  }

  getTranslation(key, fallback) {
    // In Shopify themes, translations are typically loaded via window.theme or data attributes
    // For now, using fallback values
    return fallback;
  }

  attachEventListeners() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeDrawer());
    }

    if (this.overlayEl) {
      this.overlayEl.addEventListener('click', () => this.closeDrawer());
    }

    // Touch gesture support (swipe right to close)
    this.drawerEl.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.drawerEl.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipeGesture();
    }, { passive: true });
  }

  handleSwipeGesture() {
    const swipeDistance = this.touchEndX - this.touchStartX;
    const swipeThreshold = 100; // 100px swipe distance

    if (swipeDistance > swipeThreshold) {
      this.closeDrawer();
    }
  }

  async openDrawer() {
    this.isOpen = true;
    this.isLoading = true;
    this.previouslyFocusedElement = document.activeElement;

    // Update aria-expanded on all basket icons
    document.querySelectorAll('[data-basket-icon]').forEach((icon) => {
      icon.setAttribute('aria-expanded', 'true');
    });

    // Show drawer
    this.drawerEl.removeAttribute('hidden');

    // Force reflow to ensure transition animation plays
    void this.drawerEl.offsetHeight;

    this.drawerEl.classList.add('basket-drawer--open', 'basket-drawer--loading');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Add keyboard listener
    document.addEventListener('keydown', this.handleKeydown.bind(this));

    // Fetch cart data
    try {
      this.cartData = await this.fetchCartData();

      if (this.validateCartData(this.cartData)) {
        this.renderCartContents();
      } else {
        throw new Error('Invalid cart data structure');
      }
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      this.renderErrorState();
    } finally {
      this.isLoading = false;
      this.drawerEl.classList.remove('basket-drawer--loading');

      // Focus close button for accessibility
      if (this.closeBtn) {
        this.closeBtn.focus();
      }
    }
  }

  closeDrawer() {
    this.isOpen = false;
    this.drawerEl.classList.remove('basket-drawer--open');

    // Update aria-expanded on all basket icons
    document.querySelectorAll('[data-basket-icon]').forEach((icon) => {
      icon.setAttribute('aria-expanded', 'false');
    });

    // Wait for transition to complete before hiding
    setTimeout(() => {
      this.drawerEl.setAttribute('hidden', '');
    }, 300);

    // Restore body scroll
    document.body.style.overflow = '';

    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleKeydown.bind(this));

    // Restore focus
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }
  }

  async fetchCartData() {
    const maxRetries = 1;
    const baseDelay = 1000; // 1 second

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch('/cart.js');

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const cartData = await response.json();
        return cartData;
      } catch (error) {
        if (attempt < maxRetries) {
          // Exponential backoff: Wait 1 second before retry
          const delay = baseDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          // Final attempt failed
          throw error;
        }
      }
    }
  }

  validateCartData(cartData) {
    if (!cartData || typeof cartData !== 'object') {
      return false;
    }

    if (typeof cartData.item_count !== 'number' || cartData.item_count < 0) {
      return false;
    }

    if (!Array.isArray(cartData.items)) {
      return false;
    }

    if (typeof cartData.total_price !== 'number' || cartData.total_price < 0) {
      return false;
    }

    if (typeof cartData.currency !== 'string' || !/^[A-Z]{3}$/.test(cartData.currency)) {
      return false;
    }

    return true;
  }

  renderCartContents() {
    if (this.cartData.item_count === 0) {
      this.renderEmptyState();
    } else {
      this.renderPopulatedState();
    }
  }

  /**
   * Safely clear element content
   * @param {HTMLElement} el - Element to clear
   */
  clearElement(el) {
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
  }

  /**
   * Create an element with safe text content
   * @param {string} tag - Tag name
   * @param {string} className - CSS class(es)
   * @param {string} text - Text content (safe, not HTML)
   * @returns {HTMLElement}
   */
  createElement(tag, className, text = '') {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  }

  renderEmptyState() {
    this.clearElement(this.contentEl);

    const container = this.createElement('div', 'basket-drawer__empty');
    const message = this.createElement('p', 'basket-drawer__empty-message', this.translations.emptyMessage);

    const link = document.createElement('a');
    link.href = this.emptyCta;
    link.className = 'basket-drawer__cta-button';
    link.textContent = this.translations.continueShopping;

    container.appendChild(message);
    container.appendChild(link);
    this.contentEl.appendChild(container);
  }

  renderPopulatedState() {
    this.clearElement(this.contentEl);

    // Create items list
    const itemsList = document.createElement('ul');
    itemsList.className = 'basket-drawer__items';
    this.cartData.items.forEach(item => {
      itemsList.appendChild(this.renderCartItem(item));
    });

    // Financial summary
    const subtotal = this.formatMoney(this.cartData.total_price);
    const hasDiscount = this.cartData.total_discount > 0;
    const estimatedTotal = this.formatMoney(this.cartData.total_price - this.cartData.total_discount);

    const summary = this.createElement('div', 'basket-drawer__summary');

    // Subtotal row
    const subtotalRow = this.createSummaryRow('Subtotal', subtotal);
    summary.appendChild(subtotalRow);

    // Discount rows
    if (hasDiscount) {
      if (this.cartData.cart_level_discount_applications && this.cartData.cart_level_discount_applications.length > 0) {
        this.cartData.cart_level_discount_applications.forEach(discountApp => {
          const discountRow = this.createSummaryRow(
            `Discount (${discountApp.title})`,
            `-${this.formatMoney(discountApp.value || this.cartData.total_discount)}`,
            'basket-drawer__summary-row--discount'
          );
          summary.appendChild(discountRow);
        });
      } else {
        const discountRow = this.createSummaryRow(
          'Discount',
          `-${this.formatMoney(this.cartData.total_discount)}`,
          'basket-drawer__summary-row--discount'
        );
        summary.appendChild(discountRow);
      }
    }

    // Total row
    const totalRow = this.createSummaryRow('Estimated Total', estimatedTotal, 'basket-drawer__summary-row--total');
    summary.appendChild(totalRow);

    // View basket link
    const viewBasketLink = document.createElement('a');
    viewBasketLink.href = this.cartUrl;
    viewBasketLink.className = 'basket-drawer__view-basket';
    viewBasketLink.textContent = this.translations.viewBasket;
    summary.appendChild(viewBasketLink);

    this.contentEl.appendChild(itemsList);
    this.contentEl.appendChild(summary);
  }

  /**
   * Create a summary row element
   * @param {string} label - Row label text
   * @param {string} value - Row value text
   * @param {string} extraClass - Optional extra CSS class
   * @returns {HTMLElement}
   */
  createSummaryRow(label, value, extraClass = '') {
    const row = document.createElement('div');
    row.className = 'basket-drawer__summary-row' + (extraClass ? ` ${extraClass}` : '');

    const labelSpan = this.createElement('span', 'basket-drawer__summary-label', label);
    const valueSpan = this.createElement('span', 'basket-drawer__summary-value', value);

    row.appendChild(labelSpan);
    row.appendChild(valueSpan);
    return row;
  }

  /**
   * Render a cart item as a DOM element (not HTML string)
   * @param {Object} item - Cart item data
   * @returns {HTMLElement}
   */
  renderCartItem(item) {
    const li = document.createElement('li');
    li.className = 'basket-drawer__item';

    // Image or placeholder
    if (item.image) {
      let imageUrl = item.image;
      imageUrl = imageUrl.replace(/_\d+x\d*(@2x)?\./, '.');
      imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_100x.$1');

      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = item.title;
      img.className = 'basket-drawer__item-image';
      img.loading = 'lazy';
      img.width = 80;
      img.height = 80;
      li.appendChild(img);
    } else {
      const placeholder = this.createElement('div', 'basket-drawer__item-image-placeholder', item.title.charAt(0).toUpperCase());
      li.appendChild(placeholder);
    }

    // Item details
    const details = this.createElement('div', 'basket-drawer__item-details');

    const title = document.createElement('h3');
    title.className = 'basket-drawer__item-title';
    title.title = item.title;
    title.textContent = this.truncateText(item.title, 60);
    details.appendChild(title);

    // Variant
    if (item.variant_title) {
      const variant = this.createElement('p', 'basket-drawer__item-variant', item.variant_title);
      details.appendChild(variant);
    }

    // Meta (quantity and price)
    const meta = this.createElement('div', 'basket-drawer__item-meta');
    const qty = this.createElement('span', 'basket-drawer__item-quantity', `Qty: ${item.quantity}`);
    const price = this.createElement('span', 'basket-drawer__item-price', this.formatMoney(item.line_price));
    meta.appendChild(qty);
    meta.appendChild(price);
    details.appendChild(meta);

    li.appendChild(details);
    return li;
  }

  renderErrorState() {
    this.hasError = true;
    this.clearElement(this.contentEl);

    const errorContainer = document.createElement('div');
    errorContainer.className = 'basket-drawer__error';
    errorContainer.setAttribute('role', 'alert');
    errorContainer.setAttribute('aria-live', 'assertive');

    const message = this.createElement('p', 'basket-drawer__error-message', this.translations.errorMessage);

    const retryBtn = document.createElement('button');
    retryBtn.className = 'basket-drawer__retry-button';
    retryBtn.dataset.basketRetry = '';
    retryBtn.textContent = this.translations.retryButton;
    retryBtn.addEventListener('click', () => {
      this.hasError = false;
      this.openDrawer();
    });

    errorContainer.appendChild(message);
    errorContainer.appendChild(retryBtn);
    this.contentEl.appendChild(errorContainer);
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.closeDrawer();
      return;
    }

    if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  }

  trapFocus(e) {
    const focusableElements = this.drawerEl.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstEl = focusableElements[0];
    const lastEl = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  }

  // Utility methods
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  }

  formatMoney(cents) {
    // Simple GBP formatting - hardcoded for UK market per spec assumption #1
    const pounds = (cents / 100).toFixed(2);
    return `£${pounds}`;
  }
}

class BasketIcon {
  constructor(iconEl) {
    this.iconEl = iconEl;
    this.badgeEl = iconEl.querySelector('[data-basket-count]');

    // Listen for cart update events
    document.addEventListener('cart:updated', () => this.updateBadge());
  }

  async updateBadge() {
    try {
      const response = await fetch('/cart.js');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const cartData = await response.json();

      if (this.badgeEl) {
        this.badgeEl.textContent = cartData.item_count;

        // Show/hide badge based on count
        if (cartData.item_count > 0) {
          this.badgeEl.classList.remove('basket-icon__badge--hidden');
        } else {
          this.badgeEl.classList.add('basket-icon__badge--hidden');
        }
      }
    } catch (error) {
      console.error('Failed to update basket count:', error);
      // Fail silently - user can still open drawer to see cart
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const drawerEl = document.querySelector('[data-basket-drawer]');
  const iconEls = document.querySelectorAll('[data-basket-icon]');

  if (drawerEl && iconEls.length > 0) {
    const basketDrawer = new BasketDrawer(drawerEl);

    // Expose globally for other scripts (e.g., add-to-cart)
    window.miniBasket = {
      open: () => basketDrawer.openDrawer(),
      close: () => basketDrawer.closeDrawer()
    };

    // Attach click listener to all basket icons to open drawer
    iconEls.forEach((iconEl) => {
      new BasketIcon(iconEl);
      iconEl.addEventListener('click', () => basketDrawer.openDrawer());
    });
  }
});
